/**
 * The one name a storey is given wherever a person reads it — the portal's
 * Survey page and floor plan, the plan exports and installer pack, the room
 * schedule, the customer share viewer, the heat-loss storeys the converter
 * writes. The iOS app carries a word-for-word Swift twin (`WalkStorey.name`),
 * and both are pinned to `storey-names.fixture.json` by a test on each side.
 *
 * UK usage — the ground floor is 0 and the floor above it is the first:
 *
 *     0  Ground floor
 *     1  First floor, 2 Second floor … 10 Tenth floor, then 11th floor, 12th floor …
 *    −1  Basement
 *    −2  Basement 2, −3 Basement 3 …
 *
 * Below the basement it counts ("Basement 2") rather than naming ("Lower
 * basement" / "Sub-basement"): a count goes on for as many levels as there
 * are, where a name runs out one level down ("Lower basement" leaves the level
 * under it with nothing to be called). A house with two cellars is rare, one
 * with three rarer still, and the rule should not break when it happens.
 *
 * A name somebody typed for the storey wins over all of this: `typed`, when it
 * has any text in it, is returned as it was typed (trimmed).
 */

const ORDINAL_WORDS = [
  "First",
  "Second",
  "Third",
  "Fourth",
  "Fifth",
  "Sixth",
  "Seventh",
  "Eighth",
  "Ninth",
  "Tenth",
] as const;

/** "11th", "21st", "22nd", "23rd", "111th". */
function ordinalNumber(n: number): string {
  const lastTwo = n % 100;
  if (lastTwo >= 11 && lastTwo <= 13) return `${n}th`;
  switch (n % 10) {
    case 1:
      return `${n}st`;
    case 2:
      return `${n}nd`;
    case 3:
      return `${n}rd`;
    default:
      return `${n}th`;
  }
}

/**
 * The storey's name: `typed` when someone gave it one, else the UK name for
 * its level (0 = ground, negative = below ground). Levels are whole numbers;
 * anything else is rounded to the nearest one rather than printed as
 * "Level 0.5".
 */
export function storyLabel(story: number, typed?: string | null): string {
  const own = typed?.trim();
  if (own) return own;
  const level = Number.isFinite(story) ? Math.round(story) : 0;
  if (level === 0) return "Ground floor";
  if (level === -1) return "Basement";
  if (level < 0) return `Basement ${-level}`;
  return `${ORDINAL_WORDS[level - 1] ?? ordinalNumber(level)} floor`;
}
